<script lang="ts">
	import Icon from '@iconify/svelte';
	import { page } from '$app/stores';
	import { btn } from '$lib/actions';
	import { readSettings } from '$lib/settings';
	import Avatar from './Avatar.svelte';
	import { getOrCreateUserId } from '$lib/coop.svelte';
	import { getFriends } from '$lib/friends-global.svelte';
	import type { Friends } from '$lib/friends.svelte';
	import { showToast } from '$lib/toast';
	import { sound } from '$lib/sound';

	const links = [
		{ href: '/', label: 'Games', icon: 'mdi:controller', locked: false },
		{ href: '/friends', label: 'Friends', icon: 'mdi:account-group', locked: false },
		{ href: '/leaders', label: 'Leaders', icon: 'mdi:trophy', locked: false },
		{ href: '/profile', label: 'Profile', icon: 'mdi:account-circle', locked: false }
	];

	let username = $state('');
	let avatar = $state('');
	let friends = $state<Friends | null>(null);
	let incomingCount = $derived(friends?.state.incoming.length ?? 0);

	function refreshProfile() {
		const s = readSettings();
		username = s.username || '';
		avatar = s.avatar || '';
	}

	$effect(() => {
		refreshProfile();
		window.addEventListener('storage', refreshProfile);
		// Subscribe to the (shared) friends socket. Idempotent — if the layout
		// already opened it, this returns the same instance.
		friends = getFriends(getOrCreateUserId());
		return () => window.removeEventListener('storage', refreshProfile);
	});


	let knownIncoming = new Set<string>();
	let incomingInitialized = false;
	$effect(() => {
		const current = friends?.state.incoming ?? [];
		for (const req of current) {
			if (!knownIncoming.has(req.userId)) {
				if (incomingInitialized) {
					showToast(`${req.username} sent you a friend request`, 'warning');
					sound.friendRequest();
				}
				knownIncoming.add(req.userId);
			}
		}
		incomingInitialized = true;
	});

	let outgoingNames = new Map<string, string>();
	let knownFriends = new Set<string>();
	let acceptedInitialized = false;
	$effect(() => {
		const outgoing = friends?.state.outgoing ?? [];
		const friendsList = friends?.state.friends ?? [];
		for (const req of outgoing) {
			outgoingNames.set(req.userId, req.username);
		}
		if (acceptedInitialized) {
			for (const f of friendsList) {
				if (!knownFriends.has(f.userId)) {
					if (outgoingNames.has(f.userId)) {
						showToast(`${f.username} accepted your friend request`, 'success');
					} else if (knownIncoming.has(f.userId)) {
						showToast(`You are now friends with ${f.username}`, 'success');
					}
					sound.friendAccepted();
				}
			}
		}
		for (const f of friendsList) {
			knownFriends.add(f.userId);
		}
		acceptedInitialized = true;
	});
</script>

<footer style="bottom: 0; height: calc(3.25rem + env(safe-area-inset-bottom))" class="game-card fixed right-0 left-0 z-20 w-full overflow-hidden bg-black">
	<div class="nav-inner absolute top-0 right-0 left-0 h-13 overflow-hidden">
	<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
	<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
	<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-shadow)"></div>
	<div class="pixel-frame absolute inset-2 bg-(--bottom-nav-frame-fill)"></div>
	<div class="pixel-frame absolute inset-2.5 bg-(--bottom-nav-frame-shadow)"></div>

	<div class="relative flex h-full items-center justify-center px-4">
		{#each links as link}
			{@const active = $page.url.pathname === link.href}
			{#if link.locked}
				<div aria-label={link.label} class="bottom-nav-link relative flex h-10 w-14 shrink-0 cursor-not-allowed flex-col items-center justify-center gap-0.5 opacity-35 text-(--nav-action-frame-text)">
					<div class="flex size-6 items-center justify-center">
						<Icon icon="mdi:lock" class="size-6" />
					</div>
					<span class="text-[10px] font-bold uppercase">{link.label}</span>
				</div>
			{:else}
				<div class="relative z-10 shrink-0">
					<a href={link.href} use:btn aria-label={link.label} class="bottom-nav-link relative block h-10 w-14 cursor-pointer {active ? 'opacity-100' : 'opacity-50'}">
						<div class="relative flex h-full flex-col items-center justify-center gap-0.5 text-(--nav-action-frame-text)">
							<div class="flex size-6 items-center justify-center">
								{#if link.href === '/profile'}
									<Avatar avatar={avatar} username={username} size={6} extraClass="bottomnav-avatar" />
								{:else}
									<Icon icon={link.icon} class="size-6" />
								{/if}
							</div>
							<span class="text-[10px] font-bold uppercase">{link.label}</span>
						</div>
					</a>
					{#if link.href === '/friends' && incomingCount > 0}
						<span class="count-pip pointer-events-none absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center">
							<span class="pixel-frame absolute inset-0 bg-(--present-frame-outer)"></span>
							<span class="pixel-frame absolute inset-0.5 bg-(--present-frame-highlight)"></span>
							<span class="pixel-frame absolute inset-1 bg-(--present-frame-shadow)"></span>
							<span class="pixel-frame absolute inset-1.5 bg-(--present-frame-fill)"></span>
							<span class="relative text-[11px] font-bold leading-none text-(--present-frame-text)">{incomingCount}</span>
						</span>
					{/if}
				</div>
			{/if}
		{/each}
	</div>
	</div>
</footer>
